import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { api } from '../lib/api';
import type { Cart, OptionalService } from '../types';

export function useOptionalServices() {
  return useQuery({
    queryKey: ['optional-services'],
    queryFn: async () => (await api.get<OptionalService[]>('/optional-services')).data,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCart(customerEmail?: string) {
  return useQuery({
    queryKey: ['cart', customerEmail ?? ''],
    queryFn: async () => {
      const params = customerEmail ? { customer_email: customerEmail } : undefined;
      return (await api.get<Cart>('/cart', { params })).data;
    },
  });
}

function setAllCartCaches(queryClient: ReturnType<typeof useQueryClient>, cart: Cart | null) {
  queryClient.setQueriesData<Cart | null>({ queryKey: ['cart'] }, cart);
}

export function useAddCartItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: {
      product_id: number;
      product_variant_id?: number | null;
      quantity: number;
      size_id?: number | null;
      frame_id?: number | null;
      material_id?: number | null;
      optional_service_ids?: number[];
    }) => (await api.post<Cart>('/cart/items', payload)).data,
    onSuccess: (cart) => {
      setAllCartCaches(queryClient, cart);
      toast.success('Added to cart', {
        position: 'bottom-right',
        autoClose: 2500,
        hideProgressBar: true,
        closeButton: false,
        style: {
          borderRadius: '12px',
          fontFamily: 'inherit',
          fontSize: '14px',
          fontWeight: 500,
          padding: '12px 20px',
        },
      });
    },
  });
}

export function useUpdateCartItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, quantity }: { id: number; quantity: number }) =>
      (await api.patch<Cart>(`/cart/items/${id}`, { quantity })).data,
    onSuccess: (cart) => setAllCartCaches(queryClient, cart),
  });
}

export function useRemoveCartItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => (await api.delete<Cart>(`/cart/items/${id}`)).data,
    onSuccess: (cart) => setAllCartCaches(queryClient, cart),
  });
}

export function useApplyCoupon() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ code, customer_email }: { code: string; customer_email?: string }) =>
      (await api.post<Cart>('/cart/coupon', { code, customer_email: customer_email || undefined })).data,
    onSuccess: (cart) => setAllCartCaches(queryClient, cart),
  });
}

export function useRemoveCoupon() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => (await api.delete<Cart>('/cart/coupon')).data,
    onSuccess: (cart) => setAllCartCaches(queryClient, cart),
  });
}

export function useUpdateCartBundle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, quantity }: { id: number; quantity: number }) =>
      (await api.patch<Cart>(`/cart/bundles/${id}`, { quantity })).data,
    onSuccess: (cart) => setAllCartCaches(queryClient, cart),
  });
}

export function useRemoveCartBundle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => (await api.delete<Cart>(`/cart/bundles/${id}`)).data,
    onSuccess: (cart) => setAllCartCaches(queryClient, cart),
  });
}
